import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { ConfigService } from '../shared/services/config.service';

export interface PolicyRevision {
  version: string;
  updatedAt: string;
  time: string;
  updatedBy: string;
  changes?: string;
}

export interface PolicyData {
  policyType: string;
  currentVersion: string;
  content: string;
  history: PolicyRevision[];
}

@Injectable({
  providedIn: 'root'
})
export class PolicyService {

  // Cache to store the Observables for each policy type
  private policyCache: { [key: string]: Observable<PolicyData> } = {};

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) { }

  private getApiUrl(): string {
    const baseUrl = this.configService?.apiBaseURL || '/api/v3';
    return `${baseUrl}/policies`;
  }

  /**
   * Fetches the policy data and revision history from the backend.
   * @param policyType 'cookie', 'privacy', or 'terms'
   */
  getPolicy(policyType: string): Observable<PolicyData> {
    if (!this.policyCache[policyType]) {
      const url = `${this.getApiUrl()}/${policyType}`;
      this.policyCache[policyType] = this.http.get<any>(url).pipe(
        map(res => res.responseBody),
        shareReplay(1)
      );
    }
    return this.policyCache[policyType];
  }

  /**
   * Clears the cache for a given policy type or all policies.
   */
  clearCache(policyType?: string): void {
    if (policyType) {
      delete this.policyCache[policyType];
    } else {
      this.policyCache = {};
    }
  }

  /**
   * Uploads a new .docx policy document to S3 and updates revision log.
   */
  uploadPolicy(policyType: string, fileBase64: string, originalFileName: string, changes: string): Observable<any> {
    const url = `${this.getApiUrl()}/upload`;
    const payload = {
      policyType,
      fileBase64,
      originalFileName,
      changes
    };

    return this.http.post<any>(url, payload).pipe(
      tap(() => {
        // Clear cache so UI displays updated policy immediately
        this.clearCache(policyType);
      })
    );
  }
}
